import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import keycloak from "../services/keycloak";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthContextType {
    isAuthenticated: boolean;
    token: string | null;
    logout: () => void;
    isLoading: boolean;
    keycloakInstance: typeof keycloak | null;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const clearSession = () => {
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("id_token");
};

const saveSession = (kc: typeof keycloak) => {
    if (kc.token)        sessionStorage.setItem("auth_token", kc.token);
    if (kc.refreshToken) sessionStorage.setItem("refresh_token", kc.refreshToken);
    if (kc.idToken)      sessionStorage.setItem("id_token", kc.idToken);
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [keycloakInstance, setKeycloakInstance] = useState<typeof keycloak | null>(null);
    const initialized = useRef(false);
    const MIN_VALIDITY = Number(process.env.REACT_APP_MIN_VALIDITY_TOKEN) || 70;

    // ── Initialisation Keycloak (une seule fois) ───────────────────────────────
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const storedToken   = sessionStorage.getItem("auth_token") || undefined;
        const storedRefresh = sessionStorage.getItem("refresh_token") || undefined;

        keycloak
            .init({
                onLoad: "check-sso",
                silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
                pkceMethod: "S256",
                checkLoginIframe: false,
                token: storedToken,
                refreshToken: storedRefresh,
            })
            .then((authenticated: boolean) => {
                if (authenticated && keycloak.token) {
                    saveSession(keycloak);
                    setIsAuthenticated(true);
                    setToken(keycloak.token);
                    setKeycloakInstance(keycloak);
                } else {
                    clearSession();
                    // Pas authentifié → redirection immédiate vers Keycloak
                    keycloak.login();
                }
            })
            .catch((err: unknown) => {
                console.error("Keycloak init failed:", err);
                clearSession();
                keycloak.login();
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // ── Rafraîchissement automatique du token ──────────────────────────────────
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            keycloak
                .updateToken(MIN_VALIDITY)
                .then((refreshed: boolean) => {
                    if (refreshed && keycloak.token) {
                        saveSession(keycloak);
                        setToken(keycloak.token);
                    }
                })
                .catch(() => {
                    console.error("Token refresh failed — logging out.");
                    logout();
                });
        }, 60_000); // toutes les 60s

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // ── Déconnexion ────────────────────────────────────────────────────────────
    const logout = () => {
        clearSession();
        setIsAuthenticated(false);
        setToken(null);
        keycloak.logout({ redirectUri: `${window.location.origin}/login` });
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, token, logout, isLoading, keycloakInstance }}>
            {children}
        </AuthContext.Provider>
    );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};
